import * as turf from "@turf/turf";
import gcoord from "gcoord";
import { LegendItem } from "helpers/models";
import { isOutOfChina, wgs84ToGcj02Point } from "helpers/coordinate-transform";
import {
  cellToBoundary,
  isValidCell,
  latLngToCell,
  POLYGON_TO_CELLS_FLAGS,
  polygonToCells,
  polygonToCellsExperimental
} from "h3-js";

export type PolygonAnalysisResultItem = {
  layerId?: string;
  valueLabel?: string;
  longitude?: string | number;
  latitude?: string | number;
  spatial?: string;
  index?: number | string;
  h3Index?: string;
  resolution?: number | string;
  h3Resolution?: number | string;
  h3_resolution?: number | string;
  res?: number | string;
  [key: string]: any;
};

export const groupPolygonAnalysisResultsByLayer = (results: PolygonAnalysisResultItem[] = []) => {
  const grouped = new Map<string, PolygonAnalysisResultItem[]>();

  for (const item of results) {
    const layerId = String(item?.layerId || "");
    if (!layerId) {
      continue;
    }

    const layerItems = grouped.get(layerId);
    if (layerItems) {
      layerItems.push(item);
    } else {
      grouped.set(layerId, [item]);
    }
  }

  return grouped;
};

const normalizePath = (shapeObj: any) => {
  if (!shapeObj?.getPath) {
    return [] as Array<[number, number]>;
  }

  const rawPath = shapeObj.getPath();
  if (!Array.isArray(rawPath) || !rawPath.length) {
    return [] as Array<[number, number]>;
  }

  const path = Array.isArray(rawPath[0]) ? rawPath[0] : rawPath;
  return path
    .map((point: any) => {
      const lng = Number(point?.lng ?? point?.[0]);
      const lat = Number(point?.lat ?? point?.[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return null;
      }
      return [lng, lat] as [number, number];
    })
    .filter(Boolean) as Array<[number, number]>;
};

const gcj02ToWgs84Point = (point: [number, number]): [number, number] => {
  // 境外坐标不做 GCJ02/WGS84 偏移，避免引入额外位移。
  if (isOutOfChina(point[0], point[1])) {
    return point;
  }
  const transformed = gcoord.transform(point, gcoord.GCJ02, gcoord.WGS84) as [number, number];
  return transformed;
};

const wgs84ToGcj02LngLat = (point: [number, number]): [number, number] => {
  const converted = wgs84ToGcj02Point([point[0], point[1]]);
  return [Number(converted[0]), Number(converted[1])];
};

const normalizeSpatial = (spatial: unknown): "GCJ02" | "WGS84" => {
  const normalized = String(spatial || "").toUpperCase();
  return normalized === "WGS84" || normalized === "WGS" ? "WGS84" : "GCJ02";
};

const toWgs84Point = (
  point: [number, number],
  spatial: unknown
): [number, number] => {
  if (normalizeSpatial(spatial) === "WGS84") {
    return point;
  }
  return gcj02ToWgs84Point(point);
};

const shouldUseWgsPipeline = (results: PolygonAnalysisResultItem[]) => {
  // 只要有明确 WGS84 数据，就走 WGS 管道；否则默认按地图坐标(GCJ02)空间处理。
  return results.some((item) => normalizeSpatial(item?.spatial) === "WGS84");
};

const toPoint = (item: PolygonAnalysisResultItem): [number, number] | null => {
  const longitude = Number(item?.longitude);
  const latitude = Number(item?.latitude);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    return null;
  }

  return [longitude, latitude];
};

export const normalizeResolution = (value: unknown): number | undefined => {
  const normalized = Number(value);
  if (!Number.isInteger(normalized) || normalized < 0 || normalized > 15) {
    return undefined;
  }
  return normalized;
};

const resolveH3Resolution = (
  results: PolygonAnalysisResultItem[],
  preferredResolution?: number
) => {
  const normalizedPreferred = normalizeResolution(preferredResolution);
  if (normalizedPreferred !== undefined) {
    return normalizedPreferred;
  }

  for (const item of results) {
    const candidate =
      normalizeResolution(item?.resolution) ??
      normalizeResolution(item?.h3Resolution) ??
      normalizeResolution(item?.h3_resolution) ??
      normalizeResolution(item?.res);
    if (candidate !== undefined) {
      return candidate;
    }
  }

  return undefined;
};

const buildResultCellValueLabelMap = (
  results: PolygonAnalysisResultItem[],
  resolution: number,
  useWgsPipeline: boolean
) => {
  const cellValueLabelMap = new Map<string, string>();

  for (const item of results) {
    let cellId = String(item?.h3Index || "");
    if (cellId && !isValidCell(cellId)) {
      cellId = "";
    }

    if (!cellId) {
      const point = toPoint(item);
      if (!point) {
        continue;
      }
      const [cellLng, cellLat] = useWgsPipeline
        ? toWgs84Point(point, item?.spatial)
        : point;
      cellId = latLngToCell(cellLat, cellLng, resolution);
    }

    if (!cellId || cellValueLabelMap.has(cellId)) {
      continue;
    }

    cellValueLabelMap.set(cellId, String(item?.valueLabel || ""));
  }

  return cellValueLabelMap;
};

const isSamePoint = (left: [number, number], right: [number, number]) => {
  return Math.abs(left[0] - right[0]) < 1e-12 && Math.abs(left[1] - right[1]) < 1e-12;
};

const normalizeBoundaryPath = (path: Array<[number, number]>) => {
  if (path.length > 1 && isSamePoint(path[0], path[path.length - 1])) {
    return path.slice(0, -1);
  }
  return path;
};

const closeRing = (path: Array<[number, number]>) => {
  if (!path.length) {
    return path;
  }

  const firstPoint = path[0];
  const lastPoint = path[path.length - 1];
  if (isSamePoint(firstPoint, lastPoint)) {
    return path;
  }

  return [...path, firstPoint];
};

const toTurfPolygon = (path: Array<[number, number]>) => {
  return turf.polygon([closeRing(path)] as any);
};

const extractIntersectionPaths = (
  subjectPolygon: Array<[number, number]>,
  clipPolygon: Array<[number, number]>
) => {
  if (subjectPolygon.length < 3 || clipPolygon.length < 3) {
    return [] as Array<Array<[number, number]>>;
  }

  const intersection = turf.intersect(
    turf.featureCollection([toTurfPolygon(subjectPolygon), toTurfPolygon(clipPolygon)])
  );

  if (!intersection) {
    return [] as Array<Array<[number, number]>>;
  }

  const geometry = intersection.geometry;
  if (geometry.type === "Polygon") {
    return (geometry.coordinates || [])
      .map((ring) => normalizeBoundaryPath(ring as Array<[number, number]>))
      .filter((ring) => ring.length >= 3);
  }

  if (geometry.type === "MultiPolygon") {
    return (geometry.coordinates || []).flatMap((polygon) => {
      return (polygon || [])
        .map((ring) => normalizeBoundaryPath(ring as Array<[number, number]>))
        .filter((ring) => ring.length >= 3);
    });
  }

  return [] as Array<Array<[number, number]>>;
};

const getPolygonCoverageCells = (clipPolygon: Array<[number, number]>, resolution: number) => {
  const polygonCoordinates = [clipPolygon];

  try {
    const cells = polygonToCellsExperimental(
      polygonCoordinates,
      resolution,
      POLYGON_TO_CELLS_FLAGS.containmentOverlapping,
      true
    );
    return Array.from(new Set(cells));
  } catch {
    const cells = polygonToCells(polygonCoordinates, resolution, true);
    return Array.from(new Set(cells));
  }
};

const getFallbackCellsFromResults = (
  results: PolygonAnalysisResultItem[],
  resolution: number,
  useWgsPipeline: boolean
) => {
  const cells = new Set<string>();

  for (const item of results) {
    const point = toPoint(item);
    if (!point) {
      continue;
    }

    const [cellLng, cellLat] = useWgsPipeline
      ? toWgs84Point(point, item?.spatial)
      : point;
    const cellId = latLngToCell(cellLat, cellLng, resolution);
    if (isValidCell(cellId)) {
      cells.add(cellId);
    }
  }

  return Array.from(cells);
};

const normalizeRiskLevelForLegend = (riskLevel: string, layerId: string) => {
  let normalizedRiskLevel = riskLevel;

  if (layerId.includes("Fathom")) {
    if (["5 years", "10 years", "20 years", "50 years"].includes(riskLevel)) {
      normalizedRiskLevel = "≤50 years";
    } else if (riskLevel === "1000 years") {
      normalizedRiskLevel = "≥1000 years";
    }
  } else if (layerId === "FL_Fluvial_Official" && riskLevel === "> 500 years") {
    normalizedRiskLevel = "> 500 years (US)";
  }

  return normalizedRiskLevel;
};

const getLegendStyle = (legendInfo: LegendItem | undefined, valueLabel?: string) => {
  const defaultStyle = {
    strokeColor: "#94a3b8",
    fillColor: "#cbd5e1",
    strokeOpacity: 0.7,
    fillOpacity: 0.55
  };

  if (!legendInfo?.rules?.length) {
    return defaultStyle;
  }

  const normalizedValueLabel = String(valueLabel || "").trim();

  if (
    normalizedValueLabel === "Outside" ||
    normalizedValueLabel === "No Observation"
  ) {
    return {
      strokeColor: "#f2f2f2",
      fillColor: "#f2f2f2",
      strokeOpacity: 0.5,
      fillOpacity: 0.5
    };
  }

  if (
    normalizedValueLabel === "" ||
    normalizedValueLabel === "No Data" ||
    normalizedValueLabel === "No_Data"
  ) {
    return {
      strokeColor: "grey",
      fillColor: "grey",
      strokeOpacity: 0.5,
      fillOpacity: 0.5
    };
  }

  if (normalizedValueLabel === "Waterbody") {
    return {
      strokeColor: "#DCDBDC",
      fillColor: "#DCDBDC",
      strokeOpacity: 0.5,
      fillOpacity: 0.5
    };
  }

  const layerId = String(legendInfo.layerId || "");
  const normalizedLegendValue = normalizeRiskLevelForLegend(
    normalizedValueLabel,
    layerId
  );
  const matchedRule =
    legendInfo.rules.find((rule: any) => rule?.title === normalizedLegendValue) ||
    legendInfo.rules.find((rule: any) => rule?.title === normalizedValueLabel);
  const symbol = matchedRule?.symbolizers?.[0];
  const color =
    (symbol as any)?.raster?.color ??
    (symbol as any)?.polygon?.fill ??
    defaultStyle.fillColor;
  const opacity =
    (symbol as any)?.raster?.opacity ??
    (symbol as any)?.polygon?.fillOpacity ??
    defaultStyle.fillOpacity;

  return {
    strokeColor: color,
    fillColor: color,
    strokeOpacity: Math.max(0.35, opacity),
    fillOpacity: opacity === 0 ? defaultStyle.fillOpacity : opacity
  };
};

export const buildPolygonLayerHexagons = ({
  AMap,
  results,
  legendInfo,
  shape,
  preferredResolution
}: {
  AMap: any;
  results: PolygonAnalysisResultItem[];
  legendInfo?: LegendItem;
  shape?: any;
  preferredResolution?: number;
}) => {
  if (!AMap?.Polygon || !results.length) {
    return [] as any[];
  }

  const clipPolygon = normalizePath(shape);
  if (clipPolygon.length < 3) {
    return [] as any[];
  }

  const resolution = resolveH3Resolution(results, preferredResolution);
  if (resolution === undefined) {
    return [] as any[];
  }

  const overlays: any[] = [];
  const useWgsPipeline = shouldUseWgsPipeline(results);
  const clipPolygonForH3 = useWgsPipeline
    ? clipPolygon.map((point) => gcj02ToWgs84Point(point))
    : clipPolygon;
  const coverageCells = getPolygonCoverageCells(clipPolygonForH3, resolution);
  const renderedCells = coverageCells.length
    ? coverageCells
    : getFallbackCellsFromResults(results, resolution, useWgsPipeline);
  const cellValueLabelMap = buildResultCellValueLabelMap(
    results,
    resolution,
    useWgsPipeline
  );

  for (const cellId of renderedCells) {
    if (!isValidCell(cellId)) {
      continue;
    }

    const boundary = cellToBoundary(cellId, true) as Array<[number, number]>;
    const wgsBoundary = normalizeBoundaryPath(boundary);
    const renderPaths = extractIntersectionPaths(wgsBoundary, clipPolygonForH3);
    const finalPaths = renderPaths.length ? renderPaths : [wgsBoundary];

    for (const renderPath of finalPaths) {
      if (renderPath.length < 3) {
        continue;
      }

      const gcjRenderPath = useWgsPipeline
        ? renderPath.map((point) => wgs84ToGcj02LngLat(point))
        : renderPath;
      const style = getLegendStyle(legendInfo, cellValueLabelMap.get(cellId));

      const polygon = new AMap.Polygon({
        path: gcjRenderPath.map(([lng, lat]) => new AMap.LngLat(lng, lat)),
        strokeColor: style.strokeColor,
        strokeWeight: 2,
        strokeOpacity: style.strokeOpacity,
        fillColor: style.fillColor,
        fillOpacity: 0.5,
        zIndex: 120
      });

      overlays.push(polygon);
    }
  }

  return overlays;
};